import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '@/context/GameContext';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { playClick, playSuccess } from '@/utils/sounds';
import XPRecap from '@/components/XPRecap';
import {
  RetroBackground,
  RetroPanel,
  RetroButton,
  PALETTE,
  ACCENTS,
  DESIGN_TOKENS
} from '@/components/RetroUI';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EndGame() {
  const navigate = useNavigate();
  const { 
    gameStarted,
    teamA, 
    teamB, 
    winner, 
    currentInning,
    playerStats,
    linkedAccounts,
    resetGame 
  } = useGame();
  const { user, getAuthHeader } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [xpResults, setXpResults] = useState({});
  const [showXPRecap, setShowXPRecap] = useState(null);
  const [xpAwarded, setXpAwarded] = useState(false);
  const hasTrackedCompletion = useRef(false);

  useEffect(() => {
    if (!gameStarted && !winner) {
      navigate('/dashboard');
    }
  }, [gameStarted, winner, navigate]);

  useEffect(() => {
    if (winner && !hasTrackedCompletion.current) {
      hasTrackedCompletion.current = true;
      axios.post(`${API}/games/completed`).catch(() => {});
    }
  }, [winner]);

  useEffect(() => {
    const awardAllXP = async () => {
      if (xpAwarded || !winner) return;
      setXpAwarded(true);
      
      const results = {};
      const linkedSlots = linkedAccounts || {};
      
      for (let i = 0; i < teamA.players.length; i++) {
        const slotKey = `A-${i}`;
        const linked = linkedSlots[slotKey];
        
        if (linked?.token || linked?.userId) {
          const stats = playerStats[slotKey] || {};
          const isWinner = winner === teamA.name;
          
          try {
            const result = await awardXPToSlot(linked, stats, isWinner, slotKey);
            if (result) results[slotKey] = result;
          } catch (err) {
            console.error(`Failed to award XP to ${slotKey}:`, err);
          }
        }
      }
      
      for (let i = 0; i < teamB.players.length; i++) {
        const slotKey = `B-${i}`;
        const linked = linkedSlots[slotKey];
        
        if (linked?.token || linked?.userId) {
          const stats = playerStats[slotKey] || {};
          const isWinner = winner === teamB.name;
          
          try {
            const result = await awardXPToSlot(linked, stats, isWinner, slotKey);
            if (result) results[slotKey] = result;
          } catch (err) {
            console.error(`Failed to award XP to ${slotKey}:`, err);
          }
        }
      }
      
      if (user && !linkedSlots['A-0']) {
        const hostStats = playerStats['A-0'] || {};
        const isWinner = winner === teamA.name;
        
        try {
          const response = await axios.post(`${API}/xp/award`, {
            singles: hostStats.singles || 0,
            doubles: hostStats.doubles || 0,
            triples: hostStats.triples || 0,
            home_runs: hostStats.homeRuns || 0,
            rbi: hostStats.rbi || 0,
            runs_scored: hostStats.runsScored || 0,
            outs: hostStats.outs || 0,
            game_completed: true,
            is_winner: isWinner,
            is_first_game_today: true,
          }, {
            headers: getAuthHeader()
          });
          
          results['host'] = response.data;
        } catch (err) {
          console.error('Failed to award XP to host:', err);
        }
      }
      
      setXpResults(results);
      
      const firstKey = Object.keys(results)[0];
      if (firstKey) {
        setTimeout(() => {
          setShowXPRecap(firstKey);
          playSuccess();
        }, 500);
      }
    };

    awardAllXP();
  }, [winner, xpAwarded, teamA, teamB, playerStats, linkedAccounts, user, getAuthHeader]);

  const awardXPToSlot = async (linked, stats, isWinner, slotKey) => {
    const gameStats = {
      singles: stats.singles || 0,
      doubles: stats.doubles || 0,
      triples: stats.triples || 0,
      home_runs: stats.homeRuns || 0,
      rbi: stats.rbi || 0,
      runs_scored: stats.runsScored || 0,
      outs: stats.outs || 0,
      game_completed: true,
      is_winner: isWinner,
      is_first_game_today: true,
    };
    
    if (linked.token) {
      const response = await axios.post(`${API}/xp/award`, gameStats, {
        headers: { 'Authorization': `Bearer ${linked.token}` }
      });
      return response.data;
    } else if (linked.userId) {
      const response = await axios.post(
        `${API}/xp/award-for-user/${linked.userId}`,
        gameStats,
        { headers: getAuthHeader() }
      );
      return response.data;
    }
    
    return null;
  };

  const handleSaveGame = async () => {
    playClick();
    setSaving(true);
    try {
      await axios.post(`${API}/games`, {
        team_a: {
          name: teamA.name,
          players: teamA.players,
          score: teamA.score
        },
        team_b: {
          name: teamB.name,
          players: teamB.players,
          score: teamB.score
        },
        winner: displayWinner,
        innings_played: currentInning - 1
      }, {
        headers: getAuthHeader()
      });
      setSaved(true);
      toast.success('Game saved to history!');
    } catch (error) {
      toast.error('Failed to save game');
    } finally {
      setSaving(false);
    }
  };

  const handlePlayAgain = () => {
    playClick();
    resetGame();
    navigate('/new-game');
  };

  const handleHome = () => {
    playClick();
    resetGame();
    navigate('/dashboard');
  };

  const handleXPRecapComplete = () => {
    setShowXPRecap(null);
  };

  // Fallback: game can end via songs running out before winner is set
  const displayWinner = winner || (
    teamA.score > teamB.score ? teamA.name :
    teamB.score > teamA.score ? teamB.name : 'TIE'
  );
  const isTie = displayWinner === 'TIE';

  const getTeamPlayerStats = (team) => {
    const teamData = team === 'A' ? teamA : teamB;
    return teamData.players.map((name, index) => ({
      name,
      ...playerStats[`${team}-${index}`]
    }));
  };

  // Show XP Recap as overlay
  if (showXPRecap && xpResults[showXPRecap]) {
    const result = xpResults[showXPRecap];
    const linkedUser = showXPRecap === 'host' 
      ? user 
      : (linkedAccounts?.[showXPRecap] || {});
    
    return (
      <RetroBackground variant="cream" className="p-4">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-6">
            <h1 style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '18px',
              color: PALETTE.ink,
              textShadow: `2px 2px 0px ${PALETTE.cream}`,
              marginBottom: '8px',
            }}>
              XP EARNED
            </h1>
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '10px',
              color: PALETTE.grass,
            }}>
              {linkedUser.username || 'Player'}
              {winner === teamA.name ? ' - Victory!' : winner === 'TIE' ? ' - Good game!' : ' - Better luck next time!'}
            </p>
          </div>
          
          <XPRecap 
            xpResult={result}
            onComplete={handleXPRecapComplete}
          />
        </div>
      </RetroBackground>
    );
  }

  return (
    <RetroBackground variant="greenStripes" className="p-4">
      <div className="max-w-lg mx-auto">
        {/* Final Score LED chip per reference */}
        <div className="flex justify-center mb-6">
          <div
            className="text-center px-6 py-3"
            style={{
              background: PALETTE.scoreboardBg,
              border: `4px solid ${PALETTE.ink}`,
              borderRadius: DESIGN_TOKENS.borderRadius.md,
              boxShadow: `5px 5px 0px rgba(0,0,0,0.4)`,
            }}
            data-testid="final-score-panel"
          >
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '10px',
              color: '#FFFFFF',
              letterSpacing: '2px',
              marginBottom: '6px',
            }}>
              FINAL SCORE
            </p>
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '28px',
              color: PALETTE.scoreboardText,
              textShadow: `0 0 12px ${PALETTE.scoreboardText}88`,
              marginBottom: '6px',
            }}>
              {teamA.score} - {teamB.score}
            </p>
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontSize: '11px',
              color: `${PALETTE.cream}99`,
            }}>
              {currentInning - 1} innings played
            </p>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: DESIGN_TOKENS.fontSizes.displayXl,
            color: '#E23B2E',
            textShadow: `
              -2px -2px 0px ${PALETTE.ink},
              2px -2px 0px ${PALETTE.ink},
              -2px 2px 0px ${PALETTE.ink},
              2px 2px 0px ${PALETTE.ink},
              5px 5px 0px ${PALETTE.ink},
              7px 7px 0px rgba(0,0,0,0.4)
            `,
            letterSpacing: '4px',
            marginBottom: '16px',
          }}>
            GAME OVER
          </h1>
          
          {isTie ? (
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: DESIGN_TOKENS.fontSizes.displayMd,
              color: ACCENTS.gold,
              textShadow: `2px 2px 0px ${PALETTE.ink}, 4px 4px 0px rgba(0,0,0,0.4)`,
            }}>
              IT&apos;S A TIE!
            </p>
          ) : (
            <p 
              style={{
                fontFamily: DESIGN_TOKENS.fonts.display,
                fontSize: DESIGN_TOKENS.fontSizes.displayMd,
                color: ACCENTS.gold,
                textShadow: `2px 2px 0px ${PALETTE.ink}, 4px 4px 0px rgba(0,0,0,0.4)`,
              }}
              data-testid="winner-announcement"
            >
              {displayWinner?.toUpperCase()} WINS!
            </p>
          )}
        </div>

        {/* XP Summary for all linked users */}
        {Object.keys(xpResults).length > 0 && !showXPRecap && (
          <RetroPanel title="XP AWARDED" className="mb-6">
            <div className="space-y-2">
              {Object.entries(xpResults).map(([key, result]) => {
                const linkedUser = key === 'host' 
                  ? user 
                  : (linkedAccounts?.[key] || {});
                return (
                  <div key={key} className="flex justify-between items-center">
                    <span style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                    }}>
                      {linkedUser?.username || 'Player'}
                    </span>
                    <div className="flex items-center gap-2">
                      <span style={{
                        fontFamily: DESIGN_TOKENS.fonts.ui,
                        fontSize: '11px',
                        color: PALETTE.scoreboardText,
                        textShadow: `0 0 5px ${PALETTE.scoreboardText}`,
                      }}>
                        +{result.total_xp_earned} XP
                      </span>
                      {result.levels_crossed?.length > 0 && (
                        <span style={{
                          fontFamily: DESIGN_TOKENS.fonts.ui,
                          fontSize: '11px',
                          color: PALETTE.cream,
                          background: PALETTE.grass,
                          padding: '2px 6px',
                        }}>
                          LEVEL UP!
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </RetroPanel>
        )}

        {/* Team A Stats */}
        <div 
          className="mb-4 p-4"
          style={{
            background: PALETTE.cream,
            border: `4px solid ${PALETTE.ink}`,
            borderRadius: DESIGN_TOKENS.borderRadius.lg,
            boxShadow: `6px 6px 0px rgba(0,0,0,0.4)`,
          }}
        >
          <h2 style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: '11px',
            color: PALETTE.ink,
            marginBottom: '12px',
            borderBottom: `2px solid ${PALETTE.ink}`,
            paddingBottom: '8px',
            letterSpacing: '1px',
          }}>
            {teamA.name} STATS
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `3px solid ${PALETTE.ink}` }}>
                  <th style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '11px',
                    color: PALETTE.ink,
                    textAlign: 'left',
                    padding: '8px 4px',
                  }}>PLAYER</th>
                  <th style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '11px',
                    color: PALETTE.ink,
                    textAlign: 'center',
                    padding: '8px 4px',
                  }}>1B</th>
                  <th style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '11px',
                    color: PALETTE.ink,
                    textAlign: 'center',
                    padding: '8px 4px',
                  }}>2B</th>
                  <th style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '11px',
                    color: PALETTE.ink,
                    textAlign: 'center',
                    padding: '8px 4px',
                  }}>3B</th>
                  <th style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '11px',
                    color: PALETTE.ink,
                    textAlign: 'center',
                    padding: '8px 4px',
                  }}>HR</th>
                </tr>
              </thead>
              <tbody>
                {getTeamPlayerStats('A').map((player, index) => (
                  <tr key={index} style={{ borderBottom: `1px solid ${PALETTE.ink}33` }}>
                    <td style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                      textAlign: 'left',
                      padding: '8px 4px',
                    }}>
                      {player.name}
                      {linkedAccounts?.[`A-${index}`] && (
                        <span style={{ color: PALETTE.grass, marginLeft: '4px' }}>●</span>
                      )}
                    </td>
                    <td style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                      textAlign: 'center',
                      padding: '8px 4px',
                    }}>{player.singles || 0}</td>
                    <td style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                      textAlign: 'center',
                      padding: '8px 4px',
                    }}>{player.doubles || 0}</td>
                    <td style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                      textAlign: 'center',
                      padding: '8px 4px',
                    }}>{player.triples || 0}</td>
                    <td style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                      textAlign: 'center',
                      padding: '8px 4px',
                    }}>{player.homeRuns || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Team B Stats */}
        <div 
          className="mb-8 p-4"
          style={{
            background: PALETTE.cream,
            border: `4px solid ${PALETTE.ink}`,
            borderRadius: DESIGN_TOKENS.borderRadius.lg,
            boxShadow: `6px 6px 0px rgba(0,0,0,0.4)`,
          }}
        >
          <h2 style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: '11px',
            color: PALETTE.ink,
            marginBottom: '12px',
            borderBottom: `2px solid ${PALETTE.ink}`,
            paddingBottom: '8px',
            letterSpacing: '1px',
          }}>
            {teamB.name} STATS
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `3px solid ${PALETTE.ink}` }}>
                  <th style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '11px',
                    color: PALETTE.ink,
                    textAlign: 'left',
                    padding: '8px 4px',
                  }}>PLAYER</th>
                  <th style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '11px',
                    color: PALETTE.ink,
                    textAlign: 'center',
                    padding: '8px 4px',
                  }}>1B</th>
                  <th style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '11px',
                    color: PALETTE.ink,
                    textAlign: 'center',
                    padding: '8px 4px',
                  }}>2B</th>
                  <th style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '11px',
                    color: PALETTE.ink,
                    textAlign: 'center',
                    padding: '8px 4px',
                  }}>3B</th>
                  <th style={{
                    fontFamily: DESIGN_TOKENS.fonts.ui,
                    fontSize: '11px',
                    color: PALETTE.ink,
                    textAlign: 'center',
                    padding: '8px 4px',
                  }}>HR</th>
                </tr>
              </thead>
              <tbody>
                {getTeamPlayerStats('B').map((player, index) => (
                  <tr key={index} style={{ borderBottom: `1px solid ${PALETTE.ink}33` }}>
                    <td style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                      textAlign: 'left',
                      padding: '8px 4px',
                    }}>
                      {player.name}
                      {linkedAccounts?.[`B-${index}`] && (
                        <span style={{ color: PALETTE.grass, marginLeft: '4px' }}>●</span>
                      )}
                    </td>
                    <td style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                      textAlign: 'center',
                      padding: '8px 4px',
                    }}>{player.singles || 0}</td>
                    <td style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                      textAlign: 'center',
                      padding: '8px 4px',
                    }}>{player.doubles || 0}</td>
                    <td style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                      textAlign: 'center',
                      padding: '8px 4px',
                    }}>{player.triples || 0}</td>
                    <td style={{
                      fontFamily: DESIGN_TOKENS.fonts.ui,
                      fontSize: '13px',
                      color: PALETTE.ink,
                      textAlign: 'center',
                      padding: '8px 4px',
                    }}>{player.homeRuns || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {!saved && (
            <RetroButton
              variant="secondary"
              size="md"
              fullWidth
              onClick={handleSaveGame}
              disabled={saving}
              data-testid="save-game-btn"
            >
              {saving ? 'SAVING...' : 'SAVE TO HISTORY'}
            </RetroButton>
          )}
          
          <RetroButton
            variant="primary"
            size="md"
            fullWidth
            onClick={handlePlayAgain}
            data-testid="play-again-btn"
          >
            PLAY AGAIN
          </RetroButton>
          
          <RetroButton
            variant="tertiary"
            size="md"
            fullWidth
            onClick={handleHome}
            data-testid="home-btn"
          >
            HOME
          </RetroButton>
        </div>
      </div>
    </RetroBackground>
  );
}
